import { useState } from 'react';
import toast, { type Toast } from 'react-hot-toast';
import { AlertTriangle, Check, CheckCircle2, Copy, Info, X, XCircle } from 'lucide-react';

export type NotificationVariant = 'success' | 'error' | 'warning' | 'info';

export type NotificationAction = {
  label: string;
  onClick: () => void;
};

export type NotificationProps = {
  title: string;
  message?: string;
  code?: string;
  requestId?: string;
  action?: NotificationAction;
};

const VARIANTS: Record<NotificationVariant, { accent: string; soft: string; border: string; Icon: typeof Info }> = {
  success: { accent: '#1B8A5A', soft: '#EAF8F0', border: '#BFE6D1', Icon: CheckCircle2 },
  error: { accent: '#C62828', soft: '#FDECEC', border: '#F5C2C2', Icon: XCircle },
  warning: { accent: '#B26A00', soft: '#FFF4E0', border: '#F5D9A8', Icon: AlertTriangle },
  info: { accent: '#C2185B', soft: '#FFF0F5', border: '#F0C8D8', Icon: Info },
};

export default function NotificationCard({
  t,
  variant,
  title,
  message,
  code,
  requestId,
  action,
}: NotificationProps & { t: Toast; variant: NotificationVariant }) {
  const [copied, setCopied] = useState(false);
  const v = VARIANTS[variant];
  const reference = [code, requestId && `Ref ${requestId}`].filter(Boolean).join(' · ');

  const copyReference = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the code stays visible */
    }
  };

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
      className={t.visible ? 'mic-toast-enter' : 'mic-toast-leave'}
      style={{
        width: 'min(400px, calc(100vw - 32px))',
        display: 'flex',
        background: '#fff',
        borderRadius: 16,
        border: `1px solid ${v.border}`,
        boxShadow: '0 18px 48px rgba(26,10,16,0.14), 0 4px 12px rgba(26,10,16,0.06)',
        overflow: 'hidden',
        fontFamily: 'Inter, sans-serif',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ width: 5, flexShrink: 0, background: v.accent }} />
      <div style={{ display: 'flex', gap: 12, padding: '14px 12px 14px 14px', flex: 1, minWidth: 0 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: v.soft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <v.Icon size={18} color={v.accent} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1A0A10', lineHeight: 1.35 }}>{title}</p>
          {message && (
            <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6B3A52', lineHeight: 1.5 }}>{message}</p>
          )}

          {(reference || action) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10 }}>
              {reference && (
                <button
                  type="button"
                  onClick={copyReference}
                  title="Copy error reference"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 8px',
                    borderRadius: 999,
                    border: `1px solid ${v.border}`,
                    background: v.soft,
                    color: v.accent,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    letterSpacing: '0.02em',
                    cursor: 'pointer',
                  }}
                >
                  {reference}
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                </button>
              )}
              {action && (
                <button
                  type="button"
                  onClick={() => {
                    action.onClick();
                    toast.dismiss(t.id);
                  }}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 999,
                    border: 'none',
                    background: v.accent,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {action.label}
                </button>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => toast.dismiss(t.id)}
          aria-label="Dismiss notification"
          style={{
            alignSelf: 'flex-start',
            padding: 4,
            border: 'none',
            background: 'none',
            color: '#9B6E7E',
            cursor: 'pointer',
            borderRadius: 8,
            display: 'flex',
          }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
